/**
 * Created: 2026-09-01
 * Initial commit: feat(iot): add IoT control points list page for site admins
 *
 * Page for site admins to view IoT control points (relays, sensors) at the site.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useState } from 'react'
import {
  CommandControlPointDocument,
  ControlPointAction,
  IotControlPointsDocument,
  type IotControlPointsQuery,
  PointType,
} from '../gql/graphql'
import { useRole } from '../hooks'

gql`
  query IotControlPoints($siteId: UUID!) {
    iotControlPoints(siteId: $siteId) {
      controlPointId
      type
      label
      state
      stateAt
      court {
        id
        name
      }
    }
  }
`

gql`
  mutation CommandControlPoint($controlPointId: UUID!, $action: ControlPointAction!) {
    commandControlPoint(controlPointId: $controlPointId, action: $action) {
      ... on GraphqlSuccess {
        __typename
      }
      ... on GraphqlRequestError {
        __typename
        code
        message
      }
    }
  }
`

type IotControlPoint = NonNullable<IotControlPointsQuery['iotControlPoints']>[number]

const POINT_TYPE_LABEL: Record<PointType, string> = {
  [PointType.Output]: '输出',
  [PointType.Input]: '输入',
  [PointType.Bidirectional]: '双向',
}

const baseColumns: GridColDef<IotControlPoint>[] = [
  { field: 'label', headerName: '名称', width: 200, headerAlign: 'center' },
  {
    field: 'type',
    headerName: '类型',
    width: 100,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: PointType) => POINT_TYPE_LABEL[value] ?? value,
  },
  {
    field: 'court',
    headerName: '场地',
    width: 150,
    headerAlign: 'center',
    align: 'center',
    valueGetter: (_value, row) => row.court?.name ?? '未分配',
  },
  {
    field: 'state',
    headerName: '当前状态',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: string | null) => value ?? '未知',
  },
  {
    field: 'stateAt',
    headerName: '状态更新时间',
    width: 180,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: string | null) =>
      value ? new Date(value).toLocaleString('zh-CN') : '—',
  },
]

export default function IotControlPointsPage() {
  const { currentSite } = useRole()
  const { data, loading, error, refetch } = useQuery(IotControlPointsDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })
  const [commandControlPoint] = useMutation(CommandControlPointDocument)

  // The backend doesn't yet report device-confirmed state back onto
  // `IotControlPoint.state`, so we optimistically reflect the last
  // successfully-sent command here until that lands.
  const [stateOverrides, setStateOverrides] = useState<Record<string, string>>({})
  const [pendingId, setPendingId] = useState<string | null>(null)

  const handleRefresh = () => {
    setStateOverrides({})
    refetch()
  }

  const handleToggle = async (point: IotControlPoint) => {
    const currentState = stateOverrides[point.controlPointId] ?? point.state
    const nextAction =
      currentState === 'on' ? ControlPointAction.Off : ControlPointAction.On

    setPendingId(point.controlPointId)
    try {
      const { data: mutationData } = await commandControlPoint({
        variables: { controlPointId: point.controlPointId, action: nextAction },
      })
      const result = mutationData?.commandControlPoint
      if (result?.__typename === 'GraphqlRequestError') {
        message.error(result.message ?? '指令发送失败')
        return
      }
      setStateOverrides((prev) => ({
        ...prev,
        [point.controlPointId]: nextAction === ControlPointAction.On ? 'on' : 'off',
      }))
      message.success(nextAction === ControlPointAction.On ? '已开启' : '已关闭', {
        autoHide: 2000,
      })
    } catch (err) {
      message.error(`指令发送失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setPendingId(null)
    }
  }

  if (loading) return <CircularProgress />
  if (error) return <Alert severity="error">{error.message}</Alert>

  const points = data?.iotControlPoints ?? []

  const columns: GridColDef<IotControlPoint>[] = [
    ...baseColumns,
    {
      field: 'actions',
      headerName: '操作',
      width: 90,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => {
        if (row.type !== PointType.Output) return null

        const effectiveState = stateOverrides[row.controlPointId] ?? row.state
        const isOn = effectiveState === 'on'
        const isPending = pendingId === row.controlPointId

        return (
          <Tooltip title={isOn ? '点击关闭' : '点击开启'}>
            <span>
              <IconButton
                size="small"
                disabled={isPending}
                onClick={() => handleToggle(row)}
                aria-label={isOn ? '点击关闭' : '点击开启'}
                sx={{ color: isOn ? 'success.main' : 'grey.400' }}
              >
                {isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  <PowerSettingsNewIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        设备控制点
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={handleRefresh}>
          刷新
        </Button>
      </Box>
      <DataGrid
        rows={points}
        columns={columns}
        getRowId={(row: IotControlPoint) => row.controlPointId}
        autoHeight
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
      />
    </Box>
  )
}
